import { useEffect, useState } from 'react';
import { ResendVerificationError } from '../api/emailVerifyApi';
import { useResendVerification } from './queries';

const COOLDOWN_SECONDS = 45;
const SENT_FLASH_MS = 2400;

/** Что показывать на кнопке: ошибка → отправляем → «отправлено» → обратный отсчёт → обычная подпись. */
export type ResendState = 'idle' | 'pending' | 'sent' | 'cooldown' | 'error';

/** Ключ подписи кнопки внутри `emailVerify.full` / `emailVerify.compact` (обратный отсчёт — `{{n}}`). */
export const resendLabelKey: Record<ResendState, string> = {
  idle: 'resend',
  pending: 'resending',
  sent: 'resendDone',
  cooldown: 'resendCooldown',
  error: 'resendError',
};

interface ResendCooldownOptions {
  /** Сервер ответил, что почта уже подтверждена (подтвердили в другой вкладке). */
  onAlreadyVerified?: () => void;
}

/**
 * Повторная отправка письма подтверждения с кулдауном: общий для баннера на главной
 * (EmailVerifyBanner) и блока на итоге знакомства (EmailConfirmNote).
 */
export function useResendCooldown({ onAlreadyVerified }: ResendCooldownOptions = {}) {
  const { mutateAsync, isPending } = useResendVerification();
  const [resent, setResent] = useState(false);
  const [error, setError] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    if (cooldown <= 0) return;
    const id = window.setTimeout(() => setCooldown((v) => v - 1), 1000);
    return () => window.clearTimeout(id);
  }, [cooldown]);

  useEffect(() => {
    if (!resent) return;
    const id = window.setTimeout(() => setResent(false), SENT_FLASH_MS);
    return () => window.clearTimeout(id);
  }, [resent]);

  const resend = async () => {
    if (cooldown > 0 || isPending) return;
    setError(false);
    try {
      const res = await mutateAsync();
      setResent(true);
      setCooldown(res.cooldownSeconds > 0 ? res.cooldownSeconds : COOLDOWN_SECONDS);
      if (res.status === 'already_verified') onAlreadyVerified?.();
    } catch (e) {
      // Сервер тоже держит кулдаун (429): не показываем ошибку — синхронизируем
      // таймер с серверным Retry-After. Прочие ошибки → видимое «ещё раз».
      if (e instanceof ResendVerificationError && e.code === 'COOLDOWN') {
        setCooldown(e.retryAfter && e.retryAfter > 0 ? e.retryAfter : COOLDOWN_SECONDS);
      } else {
        setError(true);
      }
    }
  };

  const state: ResendState = error
    ? 'error'
    : isPending
      ? 'pending'
      : resent
        ? 'sent'
        : cooldown > 0
          ? 'cooldown'
          : 'idle';

  return { resend, state, cooldown, disabled: cooldown > 0 || isPending };
}
